import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Input, Modal, Select, Space, Typography } from "antd";
import { useFormik } from "formik";
import { DidacticRoutesAPI } from "../../../../api";
import {
  buildInitialValues,
  validationSchema,
} from "./RutasDidacticasForm.form";

import "./RutasStep.scss";

const didacticRoutesAPI = new DidacticRoutesAPI();

export function RutasDidacticasFormStep1({
  accessToken,
  teacher,
  onRouteCreated,
  onClose,
  onReload,
  forceCreateMode,
}) {
  const { t } = useTranslation("itineraries");
  const [loading, setLoading] = useState(false);

  const classOptions = useMemo(() => {
    const roles = Array.isArray(teacher?.classrole) ? teacher.classrole : [];
    return roles
      .map((item) => {
        const value = String(item?.classnumber ?? "").trim();
        if (!value) return null;
        return { label: value, value };
      })
      .filter(Boolean);
  }, [teacher?.classrole]);

  const title = useMemo(() => {
    if (forceCreateMode) return t("wizard.createNewTitle");
    return t("wizard.editTitle");
  }, [forceCreateMode, t]);

  const formik = useFormik({
    initialValues: buildInitialValues(),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: (values) => {
      Modal.confirm({
        title: t("wizard.confirmCreateTitle"),
        content: `${t("form.confirmCreateContent")} "${values.nameRute}"?`,
        okText: t("wizard.confirmCreateOk"),
        cancelText: t("wizard.confirmCreateCancel"),
        centered: true,
        onOk: async () => {
          setLoading(true);
          try {
            const teacherId = teacher?._id;
            const institutionId = teacher?.institution?._id;

            const payload = {
              nameRute: values.nameRute,
              description: values.description,
              classnumber: values.classnumber,
              institution_Id: teacher?.institution,
            };

            const created = await didacticRoutesAPI.createDidacticRoute(
              accessToken,
              teacherId,
              institutionId,
              payload,
            );

            const newRouteId = created?.route?._id;
            if (!newRouteId) {
              Modal.error({
                title: t("wizard.createError"),
                content: t("wizard.createErrorNoId"),
                centered: true,
              });
              return;
            }

            const routeDetail = await didacticRoutesAPI.getRouteById(
              accessToken,
              newRouteId,
            );
            onRouteCreated?.(routeDetail?.route);
            onReload?.();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error al crear itinerario:", error);
            Modal.error({
              title: t("wizard.createError"),
              content: error?.message || t("wizard.unexpectedError"),
              centered: true,
            });
          } finally {
            setLoading(false);
          }
        },
      });
    },
  });

  const handleFinish = useCallback(() => {
    formik.handleSubmit();
  }, [formik]);

  const getError = useCallback(
    (field) => (formik.errors?.[field] ? String(formik.errors[field]) : null),
    [formik.errors],
  );

  return (
    <div className="rutas-step rutas-step1">
      <Typography.Title level={4} className="rutas-step__title">
        {title}
      </Typography.Title>

      <Form layout="vertical" onFinish={handleFinish}>
        <Form.Item
          label={t("form.nameLabel")}
          validateStatus={getError("nameRute") ? "error" : ""}
          help={getError("nameRute")}
        >
          <Input
            name="nameRute"
            size="large"
            value={formik.values.nameRute}
            onChange={formik.handleChange}
            placeholder={t("form.namePlaceholder")}
            autoFocus
          />
        </Form.Item>

        <Form.Item
          label={t("form.descriptionLabel")}
          validateStatus={getError("description") ? "error" : ""}
          help={getError("description")}
        >
          <Input.TextArea
            name="description"
            size="large"
            value={formik.values.description}
            onChange={formik.handleChange}
            placeholder={t("form.descriptionPlaceholder")}
            rows={4}
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Form.Item
          label={t("form.classModuleLabel")}
          validateStatus={getError("classnumber") ? "error" : ""}
          help={getError("classnumber")}
        >
          {classOptions.length > 0 ? (
            <Select
              size="large"
              placeholder={t("form.classPlaceholder")}
              value={formik.values.classnumber || undefined}
              options={classOptions}
              showSearch
              optionFilterProp="label"
              onChange={(value) => formik.setFieldValue("classnumber", value)}
            />
          ) : (
            <Input
              name="classnumber"
              size="large"
              value={formik.values.classnumber}
              onChange={formik.handleChange}
              placeholder={t("form.classModulePlaceholder")}
            />
          )}
        </Form.Item>

        <div className="rutas-step__footer">
          <Space>
            <Button onClick={onClose} size="large" disabled={loading}>
              {t("form.closeButton")}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
            >
              {t("form.createButton")}
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
}
